/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Button, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Grid, GridItem } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { gql, useMutation } from '@apollo/client';
import TextFormField from '../../components/form/TextFormField';
import GeoFormField from '../../components/form/GeoFormField';
import CheckboxFormField from '../../components/form/CheckBoxFormField';
import TextAreaFormField from '../../components/form/TextAreaFormField';
import DateFormField from '../form/DateFormField';
import MultipleFileFormField from '../form/MultipleFileFormField';
import c from '../../constant';
import * as Yup from 'yup';

const CREATE_MUTATION = gql`
	mutation create(
		$geoLocation: String
		$isGeoManual: Boolean
		$geoManual: String
		$validTo: DateTime
		$validFrom: DateTime
		$name: String!
		$nameLatin: String
		$managerName: String
		$managerNameLatin: String
		$number: String
		$companyID: String!
		$companyCertificateType: String
		$files: JSON
	) {
		createCompanyCertificate(
			geoLocation: $geoLocation
			isGeoManual: $isGeoManual
			geoManual: $geoManual
			validTo: $validTo
			validFrom: $validFrom
			name: $name
			nameLatin: $nameLatin
			managerName: $managerName
			managerNameLatin: $managerNameLatin
			number: $number
			companyID: $companyID
			companyCertificateType: $companyCertificateType
			files: $files
		)
	}
`;

const signUpSchema = Yup.object().shape({
	number: Yup.string(),
	name: Yup.string().required('Required'),
	nameLatin: Yup.string(),
	managerName: Yup.string(),
	managerNameLatin: Yup.string(),
	isGeoManual: Yup.boolean(),
	geoManual: Yup.mixed().when('isGeoManual', {
		is: (isGeoManual) => isGeoManual === true,
		then: Yup.string().required('required'),
		otherwise: Yup.string(),
	}),
	geoLocation: Yup.object().nullable(),
	validFrom: Yup.string().nullable(),
	validTo: Yup.string().nullable(),
	files: Yup.array().of(
		Yup.object().shape({
			tempFile: Yup.mixed().test('fileSize', 'required', (value) => {
				return value ? true : false;
			}),
		})
	),
});

function Create({ isOpen = false, onClose = () => null, callback = () => null, companyId }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [isGeoManual, setIsGeoManual] = useState(false);
	const [createCompanyCertificate, { loading, error, data }] = useMutation(CREATE_MUTATION);

	useEffect(() => {
		if (data) {
			if (data?.createCompanyCertificate) {
				toast({
					title: t('Company Certificate created successfully'),
					status: 'success',
					isClosable: true,
					position: 'top-right',
				});
				onClose();
				callback('Company Certificate created successfully');
			}
		}
	}, [data]);

	useEffect(() => {
		if (error) {
			error.graphQLErrors.map(({ message }, i) =>
				toast({
					title: t(message),
					status: 'error',
					isClosable: true,
					position: 'top-right',
				})
			);
		}
	}, [error]);

	return (
		<>
			<Modal isOpen={isOpen} onClose={onClose} size="4xl">
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pb="2">
					<ModalHeader>{t('Add Company Certificate')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							initialValues={{
								number: '',
								name: '',
								nameLatin: '',
								managerName: '',
								managerNameLatin: '',
								geoLocation: null,
								isGeoManual: isGeoManual,
								geoManual: '',
								validFrom: null,
								validTo: null,
								files: [],
							}}
							validationSchema={signUpSchema}
							onSubmit={(values) => {
								const variables = {
									companyCertificateType: 'b',
									companyID: companyId,
									number: values?.number,
									name: values?.name,
									nameLatin: values?.nameLatin,
									managerName: values?.managerName,
									managerNameLatin: values?.managerNameLatin,
									geoLocation: JSON.stringify(values?.geoLocation),
									isGeoManual: values?.isGeoManual,
									geoManual: values?.geoManual,
									validTo: values?.validTo,
									validFrom: values?.validFrom,
									files: values?.files,
								};
								createCompanyCertificate({ variables });
							}}
						>
							{(props) => {
								return (
									<Form>
										<Box mt="3">
											<Grid templateColumns="repeat(2, 1fr)" gap={6} w="100%">
												<GridItem w="100%">
													<TextFormField {...props} label="Number" name="number" />
													<TextFormField {...props} label="Name" name="name" required />
													<TextFormField {...props} label="Name Latin" name="nameLatin" />
													<TextFormField {...props} label="Branch Manager Name" name="managerName" />
													<TextFormField {...props} label="Branch Manager Name Latin" name="managerNameLatin" />
												</GridItem>
												<GridItem w="100%">
													{!isGeoManual && (
														<GeoFormField
															{...props}
															name="geoLocation"
															label="Address"
															buttomComponent={
																<CheckboxFormField
																	{...props}
																	label="Input"
																	name="isGeoManual"
																	callbackValueChange={(e) => setIsGeoManual(e)}
																/>
															}
														/>
													)}
													{isGeoManual && (
														<TextAreaFormField
															{...props}
															label="Address"
															name="geoManual"
															required={true}
															buttomComponent={
																<CheckboxFormField
																	{...props}
																	label="Input"
																	name="isGeoManual"
																	callbackValueChange={(e) => setIsGeoManual(e)}
																/>
															}
														/>
													)}
													<MultipleFileFormField name="files" label="Files" />
												</GridItem>
											</Grid>
											<Grid templateColumns="repeat(2, 1fr)" gap={6} w="100%">
												<GridItem w="100%">
													<DateFormField name="validFrom" label="Valid From" />
												</GridItem>
												<GridItem w="100%">
													<DateFormField name="validTo" label="Valid To" />
												</GridItem>
											</Grid>
											<Flex textAlign="right" mt="6" justifyContent="space-between" alignItems="center" float="right">
												<Button
													id="login-btn"
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													bg="primary.dark"
													_hover={{ bg: 'primary.dark' }}
													color="white"
													type="submit"
													isLoading={loading}
													loadingText={t('Add')}
													spinnerPlacement="start"
												>
													{t(`Add`)}
												</Button>
											</Flex>
										</Box>
									</Form>
								);
							}}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default Create;
